import type { OrderInfo } from '@@partner:types:app'

import React, { useState, useMemo } from 'react'
import { SelectPicker } from 'rsuite'

import LogisticKanbanColumn from './LogisticKanbanColumn'
import { sortOrders, SORT_OPTIONS, SortOption } from '@components/order/kanban/kanbanHelpers'

import './LogisticKanbanBoard.scss'

/**
 * Конфигурация группы статусов для одной колонки Kanban
 */
export interface StatusGroup {
  /** Уникальный ключ группы */
  key: string
  /** Название колонки */
  label: string
  /** Цвет бейджа / заголовка */
  color: string
  /** Статусы заказов, входящих в эту группу */
  statuses: number[]
}

export interface LogisticKanbanBoardProps {
  brandSlug: string
  orders: OrderInfo[]
  loading?: boolean
  statusGroups: StatusGroup[]
}

/**
 * Группировка заказов по группам статусов
 */
function groupOrdersByStatusGroups (
  orders: OrderInfo[],
  statusGroups: StatusGroup[]
): Map<string, OrderInfo[]> {
  const grouped = new Map<string, OrderInfo[]>()

  // Инициализация пустых массивов
  statusGroups.forEach(group => {
    grouped.set(group.key, [])
  })

  // Создаём обратный маппинг: status → groupKey
  const statusToGroup = new Map<number, string>()
  statusGroups.forEach(group => {
    group.statuses.forEach(status => {
      statusToGroup.set(status, group.key)
    })
  })

  // Распределение заказов
  orders.forEach(order => {
    const groupKey = statusToGroup.get(order.status)
    if (groupKey) {
      grouped.get(groupKey)!.push(order)
    }
  })

  return grouped
}

export default function LogisticKanbanBoard ({
  brandSlug,
  orders,
  loading,
  statusGroups
}: LogisticKanbanBoardProps) {
  const [ sortBy, setSortBy ] = useState<SortOption>('slot_time')

  // Группируем заказы по группам статусов
  const groupedOrders = useMemo(() => {
    return groupOrdersByStatusGroups(orders, statusGroups)
  }, [ orders, statusGroups ])

  // Сортируем заказы в каждой группе
  const sortedGroupedOrders = useMemo(() => {
    const sorted = new Map<string, OrderInfo[]>()
    statusGroups.forEach(group => {
      const groupOrders = groupedOrders.get(group.key) || []
      sorted.set(group.key, sortOrders(groupOrders, sortBy))
    })
    return sorted
  }, [ groupedOrders, statusGroups, sortBy ])

  return (
    <div className="LogisticKanbanBoard">
      <div className="LogisticKanbanBoard__Header">
        <SelectPicker
          data={SORT_OPTIONS}
          value={sortBy}
          onChange={(value) => setSortBy(value as SortOption)}
          cleanable={false}
          searchable={false}
          size="xs"
          style={{ width: 180 }}
          placeholder="Сортировка"
        />
      </div>
      <div className="LogisticKanbanBoard__Columns">
        {statusGroups.map(group => {
          const orders = sortedGroupedOrders.get(group.key) || []

          return (
            <LogisticKanbanColumn
              key={group.key}
              brandSlug={brandSlug}
              label={group.label}
              color={group.color}
              orders={orders}
              loading={loading}
            />
          )
        })}
      </div>
    </div>
  )
}
